import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ArrowLeft, ArrowRight } from '@mint-lib/icons';
import { Illustration } from '@mint-lib/illustrations';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  typographyVariants,
} from '@mint-lib/tw-components';

import { DotStepper } from './DotStepper.jsx';
import { getAutoImportInfoStep } from './utils.js';

type AutoImportInfoDialogProps = {
  onClose: () => void;
};

const STEPS_COUNT = 4;

export const AutoImportInfoDialog = ({
  onClose,
}: AutoImportInfoDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const [currentStep, setCurrentStep] = useState(0);
  const stepIds = Array.from(
    { length: STEPS_COUNT },
    (_, index) => `auto-import-info-step-${index}`,
  );

  const nextSlide = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const prevSlide = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="m" className="overflow-hidden">
          <DialogHeader onClose={onClose}>
            <DialogTitle>{t('Automated import guideline')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('A step-by-step guide explaining how automated imports work.')}
            </DialogDescription>
          </DialogHeader>
          <DialogContent className="relative flex-col items-center overflow-hidden p-0">
            <div
              className="flex w-full transition-transform duration-400 ease-out"
              style={{ transform: `translateX(-${currentStep * 100}%)` }}
            >
              {stepIds.map((stepId, index) => {
                const { image, title, description } = getAutoImportInfoStep(
                  t,
                  index,
                );
                return (
                  <div key={stepId} className="w-full shrink-0 p-4">
                    <div className="flex h-40 items-center justify-center">
                      <Illustration component={image} />
                    </div>
                    <div className="my-4 flex flex-col items-center gap-2">
                      <span
                        className={typographyVariants({ variant: 'h3' })}
                      >{`0${index + 1}`}</span>
                      <span className={typographyVariants({ variant: 'h3' })}>
                        {title}
                      </span>
                      <span
                        className={`${typographyVariants({ variant: 'bodyLong01' })} text-center whitespace-pre-line`}
                      >
                        {description}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>
          <DialogFooter className="h-auto flex-col items-center gap-0">
            <DotStepper steps={STEPS_COUNT} activeStep={currentStep} />
            <div className="flex flex-row items-center gap-4">
              {currentStep > 0 && (
                <Button
                  variant="text"
                  onClick={prevSlide}
                  data-dd-action-name="Back"
                >
                  <ArrowLeft className="size-4" />
                  {t('Back')}
                </Button>
              )}
              {currentStep < STEPS_COUNT - 1 ? (
                <Button onClick={nextSlide} data-dd-action-name="Next">
                  {t('Next')}
                  <ArrowRight className="size-4" />
                </Button>
              ) : (
                <Button onClick={onClose} data-dd-action-name="Got it">
                  {t('Got it')}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
